import { Fragment, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  ServiceNowConfigurationOptionsInput,
  useCreateClientIntegrationMutation,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../../utils/integrationsUtils";
import { ServiceNowFormStepOne } from "./ServiceNowFormStepOne";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type ServiceNowFormInput = {
  integrationName: string;
  instanceUrl: string;
  clientId: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
  urgency?: number;
  impact?: number;
  priority?: number;
};

const defaultValues: ServiceNowFormInput = {
  integrationName: "",
  instanceUrl: "",
  clientId: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
  urgency: undefined,
  impact: undefined,
  priority: undefined,
};

interface ServiceNowIntegrationFormProps {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}

export function ServiceNowIntegrationForm({
  scope,
  updateFlow,
}: ServiceNowIntegrationFormProps) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  // Resets the form with existing values in edit mode
  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "ServiceNowConfigurationOptions")
        return;
      reset({
        instanceUrl: configOptions.instanceUrl,
        clientId: configOptions.clientId,
        integrationName: updateFlow.integration.name,
        autoCloseTickets: configOptions.autoCloseTickets,
        autoCreateTickets: configOptions.autoCreateTickets,
      });
    }
  }, []);

  // Clears the form after successfully creating a new integration
  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<ServiceNowFormInput> = async (data) => {
    const serviceNowConfigurationOptions: ServiceNowConfigurationOptionsInput =
      {
        instanceUrl: data.instanceUrl.trim(),
        clientId: data.clientId.trim(),
        autoCloseTickets: data.autoCloseTickets,
        autoCreateTickets: data.autoCreateTickets,
      };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration?.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemServiceNow,
              configurationOptions: {
                serviceNowConfigurationOptions,
              },
            },
          },
        });
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        navigate(
          `/space/integrations/ticket_system_servicenow/${integrationId}/?${scope.params}`,
        );
      } else {
        const { data: createIntegrationData } = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemServiceNow,
              longLivedToken: false,
              configurationOptions: {
                serviceNowConfigurationOptions,
              },
            },
          },
        });
        enqueueSnackbar("Successfully created integration", {
          variant: "success",
        });
        if (redirectToParam) {
          navigate(redirectToParam);
        } else {
          navigate(
            `/space/integrations/ticket_system_servicenow?${scope.params}`,
          );
        }
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["ServiceNow", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader
        {...{ type: IntegrationType.TicketSystemServiceNow }}
      />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ServiceNowFormStepOne
            control={control}
            isUpdateFlow={!!updateFlow}
            errors={errors}
            isValid={isValid}
          />
        </form>
      </Box>
    </Fragment>
  );
}
